/**
 * The quotation grid's COLUMN LAYOUT — which order the columns sit in and how
 * wide each one is (ClickUp 86d478wd1, "modular quote generator fields").
 *
 * The grid is 24 columns of a CSS `grid`, and both the header cells and every
 * row's cells are hand-written JSX emitted in source order. Reordering by moving
 * that JSX would mean editing 48 blocks and would put the display order in two
 * places; instead the cells stay in their canonical source order and the layout
 * is applied by `QuotationGridCells`, which reorders the CHILDREN it is given.
 * That keeps this a layout concern in one module rather than a rewrite of the
 * largest render block in the codebase (CLAUDE.md: god files are strangled, not
 * rewritten).
 *
 * SAVED PER USER, on the server, with the grid's "Save columns" button (owner's
 * request, 2026-09-17), and applied to every quotation that user opens on any
 * computer — `lib/userPreferencesApi.ts` and `useQuotationGridLayout`. It is
 * still a view preference and not business data; what changed is that it
 * follows the person. It used to be remembered per BROWSER in `localStorage`,
 * written on every drag, so it was lost to a new browser or a cleared cache.
 *
 * A drag that is not saved now lasts only as long as the page. `localStorage` is
 * read once, as the starting layout for someone who arranged their columns
 * under the old behaviour and has not saved yet, and is cleared on Save or
 * Reset. Nothing writes it any more.
 */

/**
 * The default track for every column, in CANONICAL (source) order — the list
 * that used to be a literal `gridTemplateColumns` string on the page.
 *
 * This is the one place the column count is stated. A column added to or removed
 * from the grid MUST be added or removed here in the same change, or the stored
 * layouts are silently the wrong length — which `readStoredLayout` (and the
 * server-saved layout, through `usableLayout`) detects. A layout exactly ONE
 * column short is repaired rather than discarded — see `usableLayout` — but
 * that repair only holds for a column APPENDED here at the end; removing a
 * column, or inserting one anywhere but the end, still invalidates every
 * stored layout, same as always.
 */
export const QUOTATION_GRID_DEFAULT_WIDTHS: ReadonlyArray<string> = [
  '64px',  //  1 #
  '276px', //  2 Item Name / Description
  '110px', //  3 Requested Qty (RFQ)
  '100px', //  4 Quantity
  '100px', //  5 Unit
  // 6 and 10 widened 2026-09-26 (client: headers and pack text wrapped). A
  // saved layout still at the old default is widened on read — see
  // `widenOldDefaultWidths`; a width someone dragged to is kept.
  '150px', //  6 Packing size derived from pack text (was 118px)
  '130px', //  7 Equiv. per KG / per lb
  '160px', //  8 Unit Cost Price (USD) (was 102px: "US$63.6438 / CASE" ran into the next column)
  '160px', //  9 Unit Cost Price (TTD) (was 112px)
  '160px', // 10 Packing Size (Quoting) (was 130px)
  '134px', // 11 Total Cost Price (USD)
  '280px', // 12 Remarks
  '190px', // 13 Supplier
  '112px', // 14 Markup
  '146px', // 15 Unit Selling Price (USD)
  '150px', // 16 Total Selling Price (USD)
  '146px', // 17 Unit Selling Price (TTD)
  '150px', // 18 Total Cost Price (TTD)
  '150px', // 19 Total Selling Price (TTD)
  '150px', // 20 Competitor's Price (USD)
  '200px', // 21 Profit against cost price (US$ + TT$, profit ÷ cost) — was "Item Profit on Cost", before that "Item Profit (USD)"
  // 22 was Total Profit (USD): the WHOLE quote's profit, reprinted identically
  // on every line. It said nothing about the row it sat in and the Pricing
  // Summary below the grid already carries the figure, from the same call.
  '280px', // 22 Internal Remarks
  '280px', // 23 Internal More Details
  // 24 Item Profit (%) — prod ticket USC-BUG-0038 item 6: profit as a percentage
  // of the SELLING price, never of cost (Item Profit (USD)'s own parenthesised
  // figure is over cost, and the client asked for the two to be told apart).
  // Appended at the END on purpose, not next to Item Profit (USD): every
  // canonical index above still names the same column it always has, so a
  // layout saved before this shipped is one short, not reshuffled, and
  // `usableLayout` can repair it instead of discarding it (see there).
  // Renamed "Item Margin" 2026-09-25 (profit ÷ selling IS margin) and widened
  // from 110px: it now states US$ and TT$ money beside the margin, like column
  // 21 does over cost. Saved layouts still at 110px are widened on read — see
  // `widenOldDefaultWidths`.
  // Renamed "Profit against selling price" 2026-09-26 at the client's word; the
  // header still fits 200px. Canonical index unchanged, so saved layouts hold.
  '200px', // 24 Profit against selling price (US$ + TT$, profit ÷ selling)
  // 25 Competitor Markup % (D97, 2026-10-06): (competitor price - unit cost) / unit cost.
  // Appended at the END of the canonical list so saved layouts stay repairable, but
  // SHIPPED next to Competitor's Price - see `COMPETITOR_PRICE_COLUMN`.
  '120px', // 25 Competitor Markup %
];

export const QUOTATION_GRID_COLUMN_COUNT = QUOTATION_GRID_DEFAULT_WIDTHS.length;

/** Nothing narrower than this is usable, however hard someone drags. */
export const MIN_COLUMN_WIDTH_PX = 48;

/**
 * How many of the LEFTMOST columns stay put while the grid scrolls sideways.
 *
 * Two, because the pair that says WHICH LINE you are looking at is "#" and
 * "Item Name / Description", and they are the first two columns as shipped —
 * scrolling out to Item Profit (USD) left every row anonymous, which is what
 * this exists to fix.
 *
 * **Frozen by DISPLAY position, not by identity.** A frozen column pins at the
 * sum of the track widths to its left, so only a LEADING RUN can be pinned at
 * all: a column frozen in the middle of the scroll has no coherent offset and
 * would sit on top of whatever slid underneath it. So the rule is "the first two
 * columns are pinned", whichever two those are — drag another column to the
 * front and that is what freezes. That is the only form of the rule that cannot
 * break as the order changes, and the order is the user's (ClickUp 86d478wd1).
 */
export const FROZEN_COLUMN_COUNT = 2;

export interface GridLayout {
  /** Canonical column indices, in DISPLAY order. */
  order: number[];
  /** Track size per column, in CANONICAL order — not display order, so a
   *  reorder never has to rewrite the widths. */
  widths: string[];
}

// Bumped when the shape below changes incompatibly. An old value under an old
// key is simply never read again.
const STORAGE_KEY = 'usc.quotationGrid.layout.v1';

/** Canonical index of Competitor's Price (USD); Competitor Markup % is shipped right after it. */
const COMPETITOR_PRICE_COLUMN = 19;

/** Insert the Competitor Markup % column (always the LAST canonical index) right after Competitor's Price. */
function withMarkupBesideCompetitorPrice(order: number[]): number[] {
  const newColumn = QUOTATION_GRID_COLUMN_COUNT - 1;
  const at = order.indexOf(COMPETITOR_PRICE_COLUMN);
  return [...order.slice(0, at + 1), newColumn, ...order.slice(at + 1)];
}

export function defaultLayout(): GridLayout {
  return {
    order: withMarkupBesideCompetitorPrice(QUOTATION_GRID_DEFAULT_WIDTHS.slice(0, -1).map((_, i) => i)),
    widths: QUOTATION_GRID_DEFAULT_WIDTHS.slice(),
  };
}

/**
 * Is `value` a well-formed layout for a grid with exactly `count` columns?
 * Parameterised so the one-column migration below can ask "would this have
 * been a usable layout before the latest column was added" without a second
 * copy of the permutation/width checks.
 */
function isUsableForCount(value: unknown, count: number): value is GridLayout {
  if (!value || typeof value !== 'object') return false;
  const { order, widths } = value as GridLayout;
  if (!Array.isArray(order) || !Array.isArray(widths)) return false;
  // Length is the guard that matters: a column added or removed since this was
  // stored would otherwise render a grid with a hole in it, or drop a column
  // off the end.
  if (order.length !== count) return false;
  if (widths.length !== count) return false;
  // Every canonical index exactly once, or the reorder would drop or duplicate
  // a column.
  const seen = new Set<number>();
  for (const i of order) {
    if (!Number.isInteger(i) || i < 0 || i >= count) return false;
    if (seen.has(i)) return false;
    seen.add(i);
  }
  return widths.every((w) => typeof w === 'string' && w.length > 0);
}

/** Is this a layout for the grid we are actually rendering, exactly as stored? */
function isUsable(value: unknown): value is GridLayout {
  return isUsableForCount(value, QUOTATION_GRID_COLUMN_COUNT);
}

/**
 * The column count this grid had when "Item Profit (%)" was APPENDED at the
 * end (USC-BUG-0038 item 6). `migrateOneColumnShort` repairs a layout saved at
 * this EXACT count and no other, because only for this one step is every
 * earlier canonical index known to still name the column it always did:
 * Suggested Markup was once removed from the MIDDLE of the list, and Requested
 * Qty (RFQ) was once inserted at position 4 (see the numbered comments on
 * `QUOTATION_GRID_DEFAULT_WIDTHS`), and after either of those every canonical
 * index past the change point named a DIFFERENT column than it used to — a
 * layout from before either change was not "one short", it was WRONG, and
 * repairing it by appending would have silently scrambled it.
 *
 * A generic "current count minus one" check cannot tell that apart from this
 * step: the NEXT time a column is inserted or removed anywhere but the end,
 * every stored (now merely "24-long") layout would once again read as "exactly
 * one short of the new count, and otherwise a valid permutation" — and a
 * generic check would repair every one of them into a grid with every later
 * column reading someone else's values, which is worse than the discard it
 * replaced (a discard costs a drag; that costs an inexplicably scrambled grid).
 * Pinning the literal count this migration was written for means the NEXT
 * change makes it go dormant by itself instead of misfiring.
 *
 * Changing the grid again? Do NOT bump this constant to keep the migration
 * alive unless your change ALSO only appends a column at the end. If you
 * insert or remove a column anywhere but the end, DELETE this constant and
 * `migrateOneColumnShort` with it — a stored layout is then genuinely wrong,
 * not merely short, and must go back to being discarded.
 */
export const MIGRATABLE_PRIOR_COLUMN_COUNT = 24;

/**
 * A layout saved for the grid exactly ONE column narrower than it is now —
 * repaired by appending the new column to the end of both arrays, rather than
 * being discarded like every other length mismatch.
 *
 * Gated on `MIGRATABLE_PRIOR_COLUMN_COUNT`, not merely on
 * `QUOTATION_GRID_COLUMN_COUNT - 1` — see that constant for why the two must
 * not be assumed to always agree. A gap of any other size, a prior count that
 * does not match the pinned one, or a shape that fails on its own terms (a
 * duplicate index, an empty width, ...), all fall through to null.
 */
function migrateOneColumnShort(value: unknown): GridLayout | null {
  const priorCount = QUOTATION_GRID_COLUMN_COUNT - 1;
  if (
    priorCount < 1 ||
    priorCount !== MIGRATABLE_PRIOR_COLUMN_COUNT ||
    !isUsableForCount(value, priorCount)
  ) {
    return null;
  }
  const { order, widths } = value as GridLayout;
  const newColumn = QUOTATION_GRID_COLUMN_COUNT - 1;
  return {
    // Lands beside Competitor's Price wherever the person has dragged that to.
    order: withMarkupBesideCompetitorPrice(order),
    widths: [...widths, QUOTATION_GRID_DEFAULT_WIDTHS[newColumn]],
  };
}

/**
 * A layout for THIS grid, copied out of whatever it came in — a saved layout off
 * the server, or the legacy per-browser copy — or null when it does not fit.
 *
 * The server checks only the shape; the column COUNT is known here, so a layout
 * saved before a column was added or removed is discarded here and the grid
 * opens on the shipped layout rather than with a hole in it, UNLESS it is
 * exactly one column short, in which case `migrateOneColumnShort` repairs it
 * (see there for why that one case is safe). Otherwise the person re-saves.
 */
export function usableLayout(value: unknown): GridLayout | null {
  const layout = isUsable(value)
    ? { order: value.order.slice(), widths: value.widths.slice() }
    : migrateOneColumnShort(value);
  return layout && widenOldDefaultWidths(layout);
}

/**
 * Columns whose shipped default was widened, keyed by canonical index, with
 * the OLD default a saved layout may still carry.
 *  - 23 "Profit against selling price" (was "Item Margin"): 110px before
 *    2026-09-25, when it held a bare percentage; it now states US$ and TT$
 *    money beside the margin, which wraps to three or four lines at 110px.
 *  - 5 "Packing size derived from pack text" (118px) and 9 "Packing Size
 *    (Quoting)" (130px): widened 2026-09-26 at the owner's word.
 */
const OLD_DEFAULT_WIDTHS: ReadonlyArray<readonly [number, string]> = [
  [23, '110px'],
  [5, '118px'],
  [9, '130px'],
  // 2026-10-21: "Unit Cost Price" (USD) and (TTD); a four-decimal figure with its "/ CASE" suffix ran into the next column.
  [7, '102px'],
  [8, '112px'],
];

/**
 * A saved layout still carrying one of those columns at its OLD default width
 * gets the new one — a saved layout keeps its widths, so otherwise only
 * brand-new users would ever see a widened default. Only the exact old
 * default is replaced: any other width is one somebody dragged to on
 * purpose, and stays. (Someone who dragged to exactly the old default is
 * indistinguishable, and is widened too.)
 */
function widenOldDefaultWidths(layout: GridLayout): GridLayout {
  for (const [column, oldDefault] of OLD_DEFAULT_WIDTHS) {
    if (layout.widths[column] === oldDefault) {
      layout.widths[column] = QUOTATION_GRID_DEFAULT_WIDTHS[column];
    }
  }
  return layout;
}

/** Do two layouts put the same columns in the same order at the same widths? */
export function sameLayout(a: GridLayout, b: GridLayout): boolean {
  return (
    a.order.length === b.order.length &&
    a.widths.length === b.widths.length &&
    a.order.every((canonical, i) => canonical === b.order[i]) &&
    a.widths.every((w, i) => w === b.widths[i])
  );
}

/**
 * The layout remembered by the OLD per-browser behaviour, or the shipped one.
 * Read once, as the grid's starting layout — see the header.
 */
export function readStoredLayout(): GridLayout {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultLayout();
    return usableLayout(JSON.parse(raw)) ?? defaultLayout();
  } catch (_) {
    // A private window, cleared site data, or storage disabled outright. The
    // legacy copy is only a starting point — fall back to the default layout.
    return defaultLayout();
  }
}

export function clearStoredLayout(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // Storage unavailable: then there is no legacy copy to clear either.
  }
}

/** The `gridTemplateColumns` value for a layout — widths in DISPLAY order. */
export function templateColumnsFor(layout: GridLayout): string {
  return layout.order.map((canonical) => layout.widths[canonical]).join(' ');
}

/** A track's width in px, or null when it is not a plain px value. */
function trackWidthPx(width: string): number | null {
  const match = /^(\d+(?:\.\d+)?)px$/.exec(String(width ?? '').trim());
  return match ? Number(match[1]) : null;
}

/**
 * The `left` each frozen column pins at, in DISPLAY order.
 *
 * Exact arithmetic, not a measurement: every shipped width and everything
 * `resizeColumn` writes is a plain `px` string, and the cells are `box-border`,
 * so a track's width IS the cell's outer width with its borders inside it. That
 * matters because these offsets have to agree with `gridTemplateColumns` to the
 * pixel — one off by a border width and a sliver of the scrolling columns shows
 * through beside the frozen ones on every scroll.
 *
 * Returns null when an offset cannot be computed exactly. That is `isUsable`'s
 * looseness showing through — it accepts any non-empty width string, so a layout
 * written by some future version could carry `1fr` — and the caller renders an
 * ordinary UNFROZEN grid rather than pinning a column to a guess.
 *
 * THESE OFFSETS ARE NOT SUFFICIENT ON THEIR OWN. A sticky box is clamped to its
 * containing block, so the grid element itself has to be at least as wide as its
 * tracks (`minWidth: 'max-content'`, on the grid in `QuotationDetailPage`) or the
 * cells pin only within the container's own width and are then dragged off-screen
 * with the scroll — which looks exactly like the sticky CSS never having been
 * applied at all. The comment at that property records the measurements.
 */
export function frozenLeftOffsets(layout: GridLayout): number[] | null {
  const count = Math.min(FROZEN_COLUMN_COUNT, layout.order.length);
  const offsets: number[] = [];
  let left = 0;

  for (let display = 0; display < count; display += 1) {
    offsets.push(left);
    // The LAST frozen column's own width is never needed — nothing pins to the
    // right of it — so a layout is not disqualified by a width it does not use.
    if (display === count - 1) break;
    const px = trackWidthPx(layout.widths[layout.order[display]]);
    if (px === null) return null;
    left += px;
  }

  return offsets;
}

/**
 * Move the column currently shown at `fromDisplay` so that it sits at
 * `toDisplay`. Returns the SAME object when nothing moves, so a no-op drag does
 * not re-render or re-persist.
 */
export function moveColumn(layout: GridLayout, fromDisplay: number, toDisplay: number): GridLayout {
  const n = layout.order.length;
  if (fromDisplay === toDisplay) return layout;
  if (fromDisplay < 0 || fromDisplay >= n || toDisplay < 0 || toDisplay >= n) return layout;
  const order = layout.order.slice();
  const [moved] = order.splice(fromDisplay, 1);
  order.splice(toDisplay, 0, moved);
  return { ...layout, order };
}

/** Set one column's width, by CANONICAL index, floored at something usable. */
export function resizeColumn(layout: GridLayout, canonical: number, px: number): GridLayout {
  if (canonical < 0 || canonical >= layout.widths.length) return layout;
  const next = `${Math.max(MIN_COLUMN_WIDTH_PX, Math.round(px))}px`;
  if (layout.widths[canonical] === next) return layout;
  const widths = layout.widths.slice();
  widths[canonical] = next;
  return { ...layout, widths };
}

/** Put one column back to the width it ships with. */
export function resetColumnWidth(layout: GridLayout, canonical: number): GridLayout {
  if (canonical < 0 || canonical >= layout.widths.length) return layout;
  const widths = layout.widths.slice();
  widths[canonical] = QUOTATION_GRID_DEFAULT_WIDTHS[canonical];
  return { ...layout, widths };
}

/** Has the user changed anything from the shipped layout? */
export function isDefaultLayout(layout: GridLayout): boolean {
  return (
    layout.order.every((canonical, display) => canonical === defaultLayout().order[display]) &&
    layout.widths.every((w, i) => w === QUOTATION_GRID_DEFAULT_WIDTHS[i])
  );
}
